/**
 * Clause risk helpers.
 *
 * Risk is carried on `problematic_subclauses[].risk_level`, and the severity
 * chain is easy to get wrong in a way that fails silently and dangerously.
 * Every site that inspected it independently checked 'High' then 'Medium' and
 * fell through, which was correct only while 'Critical' could never occur.
 * Once it could, the most severe clauses read as the least severe - and the
 * "high risk only" filters dropped them from view altogether.
 *
 * Centralised here so the ladder is defined once.
 */

import { DetailedClauseJSON } from '../../../../types/contract.types';

export type ClauseRisk = 'critical' | 'high' | 'medium' | 'low';

/** Anything carrying the subclause shape - callers pass loosely typed data too. */
type RiskBearing = Pick<DetailedClauseJSON, 'problematic_subclauses'> | {
  problematic_subclauses?: Array<{ risk_level?: string }>;
};

/**
 * The highest severity recorded against a clause.
 *
 * Ordered worst-first: a clause with both a critical and a medium concern is
 * critical.
 */
export const clauseRiskLevel = (clause: RiskBearing): ClauseRisk => {
  const subclauses = (clause as any)?.problematic_subclauses as
    | Array<{ risk_level?: string }>
    | undefined;

  if (!subclauses?.length) return 'low';

  const has = (level: string) => subclauses.some(s => s?.risk_level === level);

  if (has('Critical')) return 'critical';
  if (has('High')) return 'high';
  if (has('Medium')) return 'medium';
  return 'low';
};

/**
 * Whether a clause is serious enough for the high-risk views, filters and
 * exports. Critical counts - excluding it is how the worst clauses disappeared
 * from the very lists meant to surface them.
 */
export const isHighRiskOrWorse = (clause: RiskBearing): boolean => {
  const level = clauseRiskLevel(clause);
  return level === 'critical' || level === 'high';
};
